import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LISTADOS, irADetalleCobro } from './definiciones';
import { toast } from 'react-toastify';
import { archivarCobro, getCobros } from '../../services/cobranzaService';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import ModalDetalleCobro from '../../components/Cobranzas/ModalDetalleCobro';
import PageHeader from '../../components/ui/PageHeader';
import useBreakpoint from '../../hooks/useBreakpoint';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';

const DEF = LISTADOS.altas;

const NUEVAS_PRIMERO = 'desc';
const VIEJAS_PRIMERO = 'asc';
const ETIQUETA_SENTIDO = {
    [NUEVAS_PRIMERO]: 'Más nuevas primero',
    [VIEJAS_PRIMERO]: 'Más viejas primero',
};

/**
 * Altas se ordena por fecha de alta, y el sentido lo elige la persona: más nuevas primero (lo de
 * siempre) o más viejas primero. En escritorio se toca el encabezado "Fecha de alta"; en celular,
 * donde las filas son tarjetas y no hay encabezados, hay un selector.
 */
export default function ListadoAltas() {
    const navigate = useNavigate();
    const { esMobile } = useBreakpoint();
    const [idCobroAbierto, setIdCobroAbierto] = useState(null);
    const [recargarToken, setRecargarToken] = useState(0);
    // Como el resto de los filtros: se recuerda al volver desde el detalle y se limpia con F5.
    const [sentido, setSentido] = useFiltroPersistente('listadoAltas.sentido', NUEVAS_PRIMERO);

    const alternarSentido = () => setSentido(sentido === NUEVAS_PRIMERO ? VIEJAS_PRIMERO : NUEVAS_PRIMERO);

    // Sin los archivados (V269): pasar a Archivo es justamente sacarlo de Altas.
    const fetchPage = (page, size, texto) => getCobros('ALTA', texto, page, size, `fechaAlta,${sentido}`, false);

    // Pasar a Archivo (V269) desde la fila: el día del cobro, haya pagado todo o quede saldo en cuotas.
    const pasarAArchivo = async (row) => {
        const nombre = [row.apellido, row.nombre].filter(Boolean).join(', ');
        if (!window.confirm(`¿Pasar a archivo el cobro de ${nombre || 'este cliente'}? Sale de Altas; si quedó saldo en cuotas sigue en Deudores.`)) return;
        try {
            await archivarCobro(row.idCobro);
            toast.success('Pasado a archivo.');
            setRecargarToken((t) => t + 1);
        } catch (err) {
            toast.error(err.response?.data?.mensaje || 'No se pudo pasar a archivo.');
        }
    };

    const columnaArchivo = {
        key: 'archivo', header: 'Archivo',
        render: (row) => (
            <button
                type="button"
                className="btn btn-sm btn-outline-secondary rounded-pill text-nowrap"
                onClick={(e) => { e.stopPropagation(); pasarAArchivo(row); }}
                title="Ya cobró: pasarlo a Archivo"
            >
                <i className="bi bi-archive me-1" />Pasar a archivo
            </button>
        ),
    };

    const columnas = useMemo(() => [...DEF.columnas.map((col) => {
        if (col.key !== 'fechaAlta') return col;
        return {
            ...col,
            header: (
                <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none fw-semibold"
                    style={{ color: 'inherit', fontSize: 'inherit' }}
                    onClick={alternarSentido}
                    title={`${ETIQUETA_SENTIDO[sentido]}. Clic para invertir el orden.`}
                    aria-label={`Fecha de alta, ordenadas: ${ETIQUETA_SENTIDO[sentido].toLowerCase()}. Clic para invertir el orden.`}
                >
                    Fecha de alta
                    <i className={`bi ${sentido === NUEVAS_PRIMERO ? 'bi-arrow-down' : 'bi-arrow-up'} ms-1`} aria-hidden="true" />
                </button>
            ),
        };
    // alternarSentido cambia con `sentido`, que ya está en las dependencias
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), columnaArchivo], [sentido]);

    const selectorOrden = esMobile ? (
        <div className="mb-3">
            <label className="form-label" htmlFor="altas-orden">
                <i className="bi bi-sort-down me-2"></i>Ordenar por fecha de alta
            </label>
            <select
                id="altas-orden"
                className="form-select"
                value={sentido}
                onChange={(e) => setSentido(e.target.value)}
            >
                <option value={NUEVAS_PRIMERO}>{ETIQUETA_SENTIDO[NUEVAS_PRIMERO]}</option>
                <option value={VIEJAS_PRIMERO}>{ETIQUETA_SENTIDO[VIEJAS_PRIMERO]}</option>
            </select>
        </div>
    ) : null;

    return (
        <div className="container-fluid mt-4 listado-pagina area-cobranzas">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            <TablaListado
                claveFiltro="listadoAltas"
                columnas={columnas}
                fetchPage={fetchPage}
                filtrosExtra={selectorOrden}
                onRowClick={(row) => irADetalleCobro(row, navigate, setIdCobroAbierto)}
                rowKey={(row) => row.idCobro}
                dependencias={[recargarToken, sentido]}
                reiniciarPaginaCon={sentido}
            />

            <ModalDetalleCobro
                open={!!idCobroAbierto}
                idCobro={idCobroAbierto}
                onClose={() => setIdCobroAbierto(null)}
                onCambio={() => setRecargarToken((t) => t + 1)}
            />
        </div>
    );
}
